/**
 * @file: useLeadActions.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * All of the lead-detail page's mutation / business-logic handlers in one hook:
 * item decisions, add-items, customer edit, lose / cancel transitions, and the
 * mobile "more" overflow menu. The page wires these to its sections.
 */

import { LeadsNavParams } from '@euler/app/flows/leads';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { usePrompt } from '@euler/functions/ux/usePrompt';
import { OrgMember } from '@euler/model/entity';
import {
  LeadIntention,
  LeadIntentionLabel,
  LeadVisibility,
} from '@euler/model/lead/Lead';
import { LeadItem, LeadItemDecision } from '@euler/model/lead/LeadItem';
import { ReservationItem } from '@euler/model/reservation/ReservationItem';
import { useServiceFactory } from '@euler/services/factory';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import format from 'date-fns/format';
import { useCallback, useRef, useState } from 'react';
import Toast from 'react-native-root-toast';

// Above this many members an action sheet is unwieldy — use the picker screen.
const kOwnerSheetThreshold = 10;

export const useLeadActions = (
  id: string,
  invalidate: () => Promise<unknown>,
  currentAssigneeId?: number,
  // The lead's vehicle, seeding the catalog picker's pending-issue scope.
  vehicle?: { licensePlateNo?: string | null; vin?: string | null },
) => {
  const { leadService, storeService } = useServiceFactory();
  const loading = useAppLoading();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const navigation = useNavigation<StackNavigationProp<LeadsNavParams>>();
  const { showActionSheetWithOptions } = useActionSheet();

  const applyDecision = useCallback(
    async (item: LeadItem, decision: LeadItemDecision | null) => {
      await loading.showWithAction(async () => {
        await leadService.setItemDecision(id, item.id, {
          customerDecision: decision,
        });
        await invalidate();
      });
    },
    [id, invalidate, leadService, loading],
  );

  const removeItem = useCallback(
    async (item: LeadItem) => {
      const ok = await confirm({
        title: `移除「${item.name}」?`,
        message: '移除后将恢复该保养项的到期提醒。',
        destructive: true,
      });
      if (!ok) return;
      await loading.showWithAction(async () => {
        await leadService.removeItem(id, item.id);
        await invalidate();
        Toast.show('已移除', { position: Toast.positions.BOTTOM });
      });
    },
    [confirm, id, invalidate, leadService, loading],
  );

  const openItemActions = useCallback(
    (item: LeadItem, canRemove: boolean) => {
      // 清除意向 (revert to 待确认) only makes sense once a decision exists;
      // 移除该项 is hidden for the last active item (the backend forbids it —
      // cancel the lead instead).
      const actions: { label: string; run: () => void }[] = [
        {
          label: '确认维修',
          run: () => void applyDecision(item, 'will_fix'),
        },
        {
          label: '暂不维修',
          run: () => void applyDecision(item, 'declined'),
        },
      ];
      if (item.customerDecision != null) {
        actions.push({
          label: '清除意向',
          run: () => void applyDecision(item, null),
        });
      }
      if (canRemove) {
        actions.push({ label: '移除该项', run: () => void removeItem(item) });
      }
      const options = [...actions.map(a => a.label), '取消'];
      showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: actions.length,
          destructiveButtonIndex: canRemove ? actions.length - 1 : undefined,
          title: item.name,
        },
        selected => {
          if (selected == null || selected >= actions.length) return;
          actions[selected].run();
        },
      );
    },
    [applyDecision, removeItem, showActionSheetWithOptions],
  );

  const runTransition = useCallback(
    async (action: 'lose' | 'cancel') => {
      const ok = await confirm({
        title: action === 'lose' ? '标记为流失?' : '取消该商机?',
        message:
          action === 'cancel' ? '取消后保养项的提醒将重新开启。' : undefined,
        destructive: true,
      });
      if (!ok) return;
      const reason = await prompt({
        title: '原因（可选）',
        defaultValue: '',
      });
      await loading.showWithAction(async () => {
        await leadService.transition(id, {
          action,
          outcomeReason: reason || undefined,
        });
        await invalidate();
        Toast.show('已更新', { position: Toast.positions.BOTTOM });
      });
    },
    [confirm, id, invalidate, leadService, loading, prompt],
  );

  // Add more uncovered same-vehicle plan details to this lead (the "从维保计划
  // 添加" path). The backend only covers `uncovered` details, so the picker's
  // selection is added as-is.
  const [addItemsShown, setAddItemsShown] = useState(false);
  const [addingItems, setAddingItems] = useState(false);
  const onAddItems = useCallback(
    async (planDetailIds: number[]) => {
      setAddingItems(true);
      try {
        await leadService.addItems(id, { planDetailIds });
        await invalidate();
        setAddItemsShown(false);
        Toast.show('已添加', { position: Toast.positions.BOTTOM });
      } catch (e) {
        console.error(e);
      } finally {
        setAddingItems(false);
      }
    },
    [id, invalidate, leadService],
  );

  // Append free catalog items (the "从项目库添加" path). The catalog picker
  // returns the full picked list; seeded empty, so all of them are new.
  const onAddCatalogItems = useCallback(
    async (items: ReservationItem[]) => {
      if (!items.length) return;
      await loading.showWithAction(async () => {
        await leadService.addCatalogItems(id, {
          items: items.map(it => ({ name: it.name, subjectType: it.type })),
        });
        await invalidate();
        Toast.show('已添加', { position: Toast.positions.BOTTOM });
      });
    },
    [id, invalidate, leadService, loading],
  );

  const openCatalogPicker = useCallback(() => {
    navigation.navigate('_pickReservationItems', {
      initialItems: [],
      vehicle: {
        licensePlateNo: vehicle?.licensePlateNo ?? undefined,
        vin: vehicle?.vin ?? undefined,
      },
      onPicked: (items: ReservationItem[]) => void onAddCatalogItems(items),
    });
  }, [navigation, onAddCatalogItems, vehicle?.licensePlateNo, vehicle?.vin]);

  // The add affordance: pick the source (maintenance plans vs. item catalog).
  const openAddItemsMenu = useCallback(() => {
    showActionSheetWithOptions(
      {
        options: ['从维保计划添加', '从项目库添加', '取消'],
        cancelButtonIndex: 2,
        title: '添加待服务项目',
      },
      selected => {
        if (selected === 0) setAddItemsShown(true);
        else if (selected === 1) openCatalogPicker();
      },
    );
  }, [openCatalogPicker, showActionSheetWithOptions]);

  // Manual visit-intention (到店意向) override. Picking a level marks the lead
  // overridden so the auto-derivation from outreach outcomes stops touching it.
  const applyIntention = useCallback(
    async (intention: LeadIntention) => {
      await loading.showWithAction(async () => {
        await leadService.setIntention(id, intention);
        await invalidate();
        Toast.show('已更新意向', { position: Toast.positions.BOTTOM });
      });
    },
    [id, invalidate, leadService, loading],
  );

  const openIntentionPicker = useCallback(() => {
    const levels: LeadIntention[] = [
      'high',
      'medium',
      'low',
      'none',
      'undetermined',
    ];
    const options = [...levels.map(l => LeadIntentionLabel[l]), '取消'];
    showActionSheetWithOptions(
      { options, cancelButtonIndex: levels.length, title: '设置到店意向' },
      selected => {
        if (selected == null || selected >= levels.length) return;
        void applyIntention(levels[selected]);
      },
    );
  }, [applyIntention, showActionSheetWithOptions]);

  // Reassign the lead's owner (负责人). The roster is fetched lazily on first
  // open so the detail screen never suspends; a short roster picks via an action
  // sheet, a longer one routes to the searchable picker screen.
  const ownerRoster = useRef<OrgMember[] | null>(null);
  const applyOwner = useCallback(
    async (member: OrgMember) => {
      if (member.id === currentAssigneeId) return;
      await loading.showWithAction(async () => {
        await leadService.update(id, {
          assigneeId: member.id,
          assigneeName: member.name,
        });
        await invalidate();
        Toast.show('已更换负责人', { position: Toast.positions.BOTTOM });
      });
    },
    [currentAssigneeId, id, invalidate, leadService, loading],
  );

  const openOwnerPicker = useCallback(async () => {
    if (!ownerRoster.current) {
      await loading.showWithAction(async () => {
        ownerRoster.current = await storeService.listServiceAgents();
      });
    }
    const members = ownerRoster.current ?? [];
    if (members.length < kOwnerSheetThreshold) {
      const labels = members.map(m => m.name);
      showActionSheetWithOptions(
        {
          options: [...labels, '取消'],
          cancelButtonIndex: labels.length,
          title: '负责人',
        },
        selected => {
          if (selected == null || selected >= labels.length) return;
          void applyOwner(members[selected]);
        },
      );
    } else {
      navigation.push('_serviceAgentSelector', {
        selectedServiceAgentId: currentAssigneeId,
        onSelect: (m: OrgMember) => void applyOwner(m),
      });
    }
  }, [
    applyOwner,
    currentAssigneeId,
    loading,
    navigation,
    showActionSheetWithOptions,
    storeService,
  ]);

  // Change who may see the lead. An action sheet can't explain the levels, so
  // the picker is a BottomPanel (rendered by the screen) toggled by this state.
  const [editingVisibility, setEditingVisibility] = useState(false);
  const onSaveVisibility = useCallback(
    async (visibility: LeadVisibility) => {
      setEditingVisibility(false);
      await loading.showWithAction(async () => {
        await leadService.update(id, { visibility });
        await invalidate();
        Toast.show('已更新可见范围', { position: Toast.positions.BOTTOM });
      });
    },
    [id, invalidate, leadService, loading],
  );

  // Reschedule the target service date (下次保养). The backend re-derives
  // nothing else (outreach / expiry stay put) and the reminder chain re-reads
  // the new date; it logs a `target_date_changed` activity when it actually
  // moves.
  const [editingTargetDate, setEditingTargetDate] = useState(false);
  const onSaveTargetDate = useCallback(
    async (date: Date) => {
      setEditingTargetDate(false);
      await loading.showWithAction(async () => {
        await leadService.update(id, {
          targetServiceDate: format(date, 'yyyy-MM-dd'),
        });
        await invalidate();
        Toast.show('已更新目标服务日期', { position: Toast.positions.BOTTOM });
      });
    },
    [id, invalidate, leadService, loading],
  );

  const [editingCustomer, setEditingCustomer] = useState(false);
  const onSaveCustomer = useCallback(
    async (name: string, phone: string) => {
      await loading.showWithAction(async () => {
        await leadService.update(id, {
          customerName: name.trim() || null,
          customerPhone: phone.trim() || null,
        });
        await invalidate();
        setEditingCustomer(false);
        Toast.show('已更新', { position: Toast.positions.BOTTOM });
      });
    },
    [id, invalidate, leadService, loading],
  );

  // Mobile folds 标记流失 / 取消商机 behind the nav-bar three-dots; desktop
  // surfaces them inline next to the primary CTA (see the action row below).
  const openMoreMenu = useCallback(() => {
    showActionSheetWithOptions(
      {
        options: ['标记流失', '取消商机', '取消'],
        cancelButtonIndex: 2,
        destructiveButtonIndex: 1,
      },
      selected => {
        if (selected === 0) void runTransition('lose');
        else if (selected === 1) void runTransition('cancel');
      },
    );
  }, [runTransition, showActionSheetWithOptions]);

  return {
    openItemActions,
    runTransition,
    openMoreMenu,
    addItems: {
      shown: addItemsShown,
      submitting: addingItems,
      openMenu: openAddItemsMenu,
      close: () => setAddItemsShown(false),
      confirm: onAddItems,
    },
    customer: {
      editing: editingCustomer,
      open: () => setEditingCustomer(true),
      close: () => setEditingCustomer(false),
      save: onSaveCustomer,
    },
    targetDate: {
      editing: editingTargetDate,
      open: () => setEditingTargetDate(true),
      close: () => setEditingTargetDate(false),
      save: onSaveTargetDate,
    },
    intention: { open: openIntentionPicker },
    owner: { open: openOwnerPicker },
    visibility: {
      editing: editingVisibility,
      open: () => setEditingVisibility(true),
      close: () => setEditingVisibility(false),
      save: onSaveVisibility,
    },
  };
};
